import { useEffect, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import RightTimeApi from "@/API";

/**
 * Connects to the Azure SignalR Service dashboard hub and calls `onUpdate`
 * whenever a `testStarted`, `testGraded`, `testCreated`, `testCompleted`, or `auditError` message arrives.
 *
 * Automatically tears down the connection on unmount.
 * The callback ref pattern ensures `onUpdate` is never stale without
 * restarting the connection.
 *
 * @param onUpdate  Called with `{ companyId, companyName, testId, firstName, lastName }` from the server message.
 *                  Pass `undefined` companyId = system-wide change.
 */
export function useDashboardSignalR(
    onUpdate: (data?: {
        companyId?: number;
        companyName?: string;
        testId?: number;
        firstName?: string;
        lastName?: string;
        userName?: string;
        eventType?: string;
        receivedAt?: string;
        id?: number;
        severity?: number;
        message?: string;
        context?: string;
        when?: string;
        entityTypeId?: number;
        entityId?: number;
    }) => void,
    options?: { enabled?: boolean }
) {
    const enabled = options?.enabled !== false;
    const onUpdateRef = useRef(onUpdate);

    // Keep the callback current on every render without restarting the hub connection
    useEffect(() => {
        onUpdateRef.current = onUpdate;
    });

    useEffect(() => {
        if (!enabled) return;

        let connection: signalR.HubConnection | null = null;
        let cancelled = false;

        async function start() {
            const resp = await RightTimeApi.getAsync("/negotiate/dashboard");
            if (cancelled) return;
            if (!resp?.ok || !resp.jsonData) {
                console.warn("SignalR negotiate failed — live dashboard updates unavailable.");
                return;
            }

            const { url, accessToken } = resp.jsonData;
            if (!url) return;

            connection = new signalR.HubConnectionBuilder()
                .withUrl(url, {
                    accessTokenFactory: () => accessToken,
                })
                .withAutomaticReconnect()
                .configureLogging(signalR.LogLevel.Warning)
                .build();

            connection.on("testGraded", (data) => onUpdateRef.current({ ...data, eventType: "testGraded", receivedAt: new Date().toISOString() }));
            connection.on("testCreated", (data) => onUpdateRef.current({ ...data, eventType: "testCreated", receivedAt: new Date().toISOString() }));
            connection.on("testCompleted", (data) => onUpdateRef.current({ ...data, eventType: "testCompleted", receivedAt: new Date().toISOString() }));
            connection.on("testStarted", (data) => onUpdateRef.current({ ...data, eventType: "testStarted", receivedAt: new Date().toISOString() }));
            connection.on("auditError", (data) => onUpdateRef.current({ ...data, eventType: "auditError", receivedAt: new Date().toISOString() }));

            try {
                await connection.start();
            } catch (e) {
                if (!cancelled) {
                    console.warn("SignalR connection error:", e);
                }
            }
        }

        void start();

        return () => {
            cancelled = true;
            connection?.stop();
        };
    }, [enabled]);
}
